/**
 * הדפסת דוחות חברים — רשימה / פילוח / כרטיס
 */
import { REPORT_BLANK_PRINT_CSS } from './memberReportBlanks.js';
import {
  applyMemberMergeTemplateClient,
  buildRegistrationsTableHtml,
  buildGradesTableHtml,
  defaultTemplateForType,
} from './memberReportMergeFields.js';
import { formatHebrewDateTime } from '../../../utils/hebrewDate';

export const DEFAULT_LETTER_INSETS = Object.freeze({
  topMm: 45,
  rightMm: 18,
  bottomMm: 15,
  leftMm: 18,
});

export function normalizeLetterInsets(raw = {}) {
  const src = raw && typeof raw === 'object' ? raw : {};
  const clamp = (v, fb, max) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return fb;
    return Math.min(max, Math.max(0, Math.round(n * 10) / 10));
  };
  return {
    topMm: clamp(src.topMm, DEFAULT_LETTER_INSETS.topMm, 120),
    rightMm: clamp(src.rightMm, DEFAULT_LETTER_INSETS.rightMm, 80),
    bottomMm: clamp(src.bottomMm, DEFAULT_LETTER_INSETS.bottomMm, 80),
    leftMm: clamp(src.leftMm, DEFAULT_LETTER_INSETS.leftMm, 80),
  };
}

export function letterInsetsCssValue(insets) {
  const i = normalizeLetterInsets(insets);
  return `${i.topMm}mm ${i.rightMm}mm ${i.bottomMm}mm ${i.leftMm}mm`;
}

export const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

export function formatMemberCell(key, val) {
  if (val == null || val === '') return '—';
  if (Array.isArray(val)) return val.filter(Boolean).join(', ') || '—';
  if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}/.test(val)) return val.slice(0, 10);
  if (typeof val === 'number') return val.toLocaleString('he-IL');
  return String(val);
}

function formatReportCell(column, row) {
  if (column.custom) return '';
  return formatMemberCell(column.key, row[column.key]);
}

/**
 * CSS לעמוד מכתב — משותף להדפסה ולתצוגה מקדימה בעורך.
 * @param {{ topMm?: number, rightMm?: number, bottomMm?: number, leftMm?: number }} [letterInsets]
 */
export function letterPageCss(letterInsets) {
  const pad = letterInsetsCssValue(letterInsets);
  return `
    .letter-page {
      page-break-after: always;
      padding: 8mm 6mm 4mm;
      min-height: 260mm;
      position: relative;
      box-sizing: border-box;
      background-color: #fff;
    }
    .letter-page.has-blank {
      padding: 0;
      background-repeat: no-repeat;
      background-position: center top;
      background-size: 100% 100%;
    }
    .letter-page.has-blank .letter-body {
      position: relative;
      z-index: 1;
      padding: ${pad};
      box-sizing: border-box;
      min-height: 260mm;
    }
    .letter-body { font-size: 12pt; line-height: 1.65; }
    .letter-body p { margin: 0 0 10px; }
    table.member-merge-table { width: 100%; border-collapse: collapse; font-size: 11px; margin: 8px 0; }
    table.member-merge-table th, table.member-merge-table td {
      border: 1px solid #ccc; padding: 3px 5px; text-align: right;
    }
    table.member-merge-table th { background: #f3f3f3; }
  `;
}

export function wrapLetterPageHtml(bodyInnerHtml, reportBlankUrl = '') {
  const hasBlank = Boolean(reportBlankUrl);
  const blankStyle = hasBlank
    ? ` style="background-image:url('${String(reportBlankUrl).replace(/'/g, '%27')}');"`
    : '';
  const pageClass = hasBlank ? 'letter-page has-blank' : 'letter-page';
  return `<section class="${pageClass}"${blankStyle}><div class="letter-body">${bodyInnerHtml || ''}</div></section>`;
}

export function openMemberPrintWindow(title, bodyHtml, styles, { width = 1100, height = 800 } = {}) {
  const printWindow = window.open('', '_blank', `width=${width},height=${height}`);
  if (!printWindow) {
    window.alert('לא ניתן לפתוח חלון הדפסה — בדוק חוסם pop-up');
    return false;
  }
  printWindow.document.write(`<!DOCTYPE html>
<html dir="rtl" lang="he">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(title)}</title>
  <style>${styles}</style>
</head>
<body>${bodyHtml}</body>
</html>`);
  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => {
    printWindow.print();
    printWindow.close();
  }, 400);
  return true;
}

function listStyles(letterInsets) {
  return `
    @page { margin: 10mm 8mm; size: A4 landscape; }
    * { box-sizing: border-box; }
    body {
      font-family: "David", "Arial Hebrew", Arial, sans-serif;
      direction: rtl; color: #111; margin: 0; padding: 0; font-size: 11px;
      -webkit-print-color-adjust: exact; print-color-adjust: exact;
    }
    .rpt-header { margin-bottom: 12px; border-bottom: 1px solid #ccc; padding-bottom: 8px; }
    .rpt-title { font-size: 18px; font-weight: 700; margin: 0 0 4px; }
    .rpt-meta { color: #555; margin: 0; font-size: 11px; }
    table.rpt-table { width: 100%; border-collapse: collapse; table-layout: fixed; }
    table.rpt-table th, table.rpt-table td {
      border: 1px solid #bbb; padding: 3px 5px; text-align: right; vertical-align: top;
      word-wrap: break-word;
    }
    table.rpt-table th { background: #f0f0f0; font-weight: 700; font-size: 10px; }
    table.rpt-table tr:nth-child(even) td { background: #fafafa; }
    table.rpt-table tr.child-row td { background: #f7f7f7; font-size: 10px; color: #333; }
    ${letterPageCss(letterInsets)}
    ${REPORT_BLANK_PRINT_CSS}
  `;
}

function buildListTableHtml({ title, filterSummary = [], columns = [], rows = [], includeRegistrations = false }) {
  const produced = formatHebrewDateTime(new Date());
  const filtersLine = filterSummary.length
    ? `<p class="rpt-meta">סינונים: ${filterSummary.map(escapeHtml).join(' · ')}</p>`
    : '';
  const head = columns.map((c) => `<th>${escapeHtml(c.label)}</th>`).join('');
  const colCount = Math.max(columns.length, 1);
  const body = rows.map((row) => {
    const cells = columns
      .map((c) => `<td>${escapeHtml(formatReportCell(c, row))}</td>`)
      .join('');
    const main = `<tr>${cells}</tr>`;
    if (!includeRegistrations) return main;
    const regs = row.registrations || [];
    if (!regs.length) return main;
    const line = regs.map((r) => r.examName || (r.gemaraSelection || []).map((g) => g.tractate).join(',') || 'רישום').join(' · ');
    return `${main}<tr class="child-row"><td colspan="${colCount}">רישומים: ${escapeHtml(line)}</td></tr>`;
  }).join('');
  return `
    <div class="rpt-header">
      <h1 class="rpt-title">${escapeHtml(title)}</h1>
      <p class="rpt-meta">${rows.length.toLocaleString('he-IL')} רשומות · הופק: ${escapeHtml(produced)}</p>
      ${filtersLine}
    </div>
    <table class="rpt-table"><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`;
}

export function printMemberList(opts = {}) {
  const docTitle = opts.title || 'רשימת חברים';
  const letterHtml = opts.letterHtml || defaultTemplateForType('list');
  const insets = normalizeLetterInsets(opts.letterInsets);
  const merged = applyMemberMergeTemplateClient(letterHtml, {}, {
    record_count: (opts.rows || []).length,
    period_name: opts.periodName || '',
  });
  const letter = wrapLetterPageHtml(merged, opts.blankUrl);
  const table = buildListTableHtml({
    title: docTitle,
    filterSummary: opts.filterSummary || [],
    columns: opts.columns || [],
    rows: opts.rows || [],
    includeRegistrations: !!opts.includeRegistrations,
  });
  openMemberPrintWindow(docTitle, `${letter}<div>${table}</div>`, listStyles(insets));
}

function breakdownStyles(letterInsets) {
  return `
    @page { margin: 10mm 8mm; size: A4 landscape; }
    * { box-sizing: border-box; }
    body {
      font-family: "David", "Arial Hebrew", Arial, sans-serif;
      direction: rtl; color: #111; margin: 0; padding: 0; font-size: 11px;
      -webkit-print-color-adjust: exact; print-color-adjust: exact;
    }
    .rpt-header { margin-bottom: 12px; border-bottom: 1px solid #ccc; padding-bottom: 8px; }
    .rpt-title { font-size: 18px; font-weight: 700; margin: 0 0 4px; }
    .rpt-meta { color: #555; margin: 0; font-size: 11px; }
    .group-block { margin-bottom: 16px; page-break-inside: avoid; }
    .group-head {
      display: flex; justify-content: space-between; gap: 12px;
      background: #f0f0f0; border: 1px solid #bbb; padding: 6px 8px; font-weight: 700; font-size: 13px;
    }
    table.break-table { width: 100%; border-collapse: collapse; table-layout: fixed; margin-top: 0; }
    table.break-table th, table.break-table td {
      border: 1px solid #bbb; padding: 3px 5px; text-align: right; vertical-align: top;
    }
    ${letterPageCss(letterInsets)}
    ${REPORT_BLANK_PRINT_CSS}
  `;
}

function buildMemberTable(members, columns) {
  if (!members?.length || !columns?.length) return '';
  const head = columns.map((c) => `<th>${escapeHtml(c.label)}</th>`).join('');
  const body = members.map((row) => {
    const cells = columns
      .map((c) => `<td>${escapeHtml(formatReportCell(c, row))}</td>`)
      .join('');
    return `<tr>${cells}</tr>`;
  }).join('');
  return `<table class="break-table"><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`;
}

export function printMemberBreakdown(opts = {}) {
  const title = opts.title || 'פילוח חברים';
  const produced = formatHebrewDateTime(new Date());
  const insets = normalizeLetterInsets(opts.letterInsets);
  const filtersLine = (opts.filterSummary || []).length
    ? `<p class="rpt-meta">סינונים: ${opts.filterSummary.map(escapeHtml).join(' · ')}</p>`
    : '';
  const letterHtml = opts.letterHtml || defaultTemplateForType('breakdown');
  const merged = applyMemberMergeTemplateClient(letterHtml, {}, {
    record_count: (opts.groups || []).reduce((s, g) => s + (g.count || 0), 0),
  });
  const letter = wrapLetterPageHtml(merged, opts.blankUrl);
  const groupsHtml = (opts.groups || []).map((g) => `
    <div class="group-block">
      <div class="group-head">
        <span>${escapeHtml(g.label)}</span>
        <span>${Number(g.count || 0).toLocaleString('he-IL')} חברים</span>
      </div>
      ${buildMemberTable(g.members || [], opts.columns || [])}
    </div>
  `).join('');
  const header = `
    <div class="rpt-header">
      <h1 class="rpt-title">${escapeHtml(title)}</h1>
      <p class="rpt-meta">לפי ${escapeHtml(opts.groupByLabel || '')} · הופק: ${escapeHtml(produced)}</p>
      ${filtersLine}
    </div>`;
  openMemberPrintWindow(title, `${letter}${header}${groupsHtml}`, breakdownStyles(insets));
}

function cardStyles(letterInsets) {
  return `
    @page { margin: 12mm 10mm; size: A4 portrait; }
    * { box-sizing: border-box; }
    body {
      font-family: "David", "Arial Hebrew", Arial, sans-serif;
      direction: rtl; color: #111; margin: 0; padding: 0; font-size: 12px;
      -webkit-print-color-adjust: exact; print-color-adjust: exact;
    }
    .card-pack { page-break-after: always; }
    .card-pack:last-child { page-break-after: auto; }
    ${letterPageCss(letterInsets)}
    ${REPORT_BLANK_PRINT_CSS}
  `;
}

export function printMemberCards(opts = {}) {
  const title = opts.title || 'כרטיס נבחן';
  const tpl = opts.letterHtml || defaultTemplateForType('card');
  const insets = normalizeLetterInsets(opts.letterInsets);
  const cards = (opts.rows || []).map((row) => {
    const registrationsTable = opts.includeRegistrations !== false
      ? buildRegistrationsTableHtml(row.registrations || [])
      : '';
    const gradesTable = opts.includeGrades !== false
      ? buildGradesTableHtml(row.grades || [])
      : '';
    const body = applyMemberMergeTemplateClient(tpl, row, {
      record_count: (opts.rows || []).length,
      period_name: opts.periodName || '',
      registrations_table: registrationsTable,
      grades_table: gradesTable,
    });
    return `<div class="card-pack">${wrapLetterPageHtml(body, opts.blankUrl)}</div>`;
  }).join('');
  openMemberPrintWindow(title, cards, cardStyles(insets), { width: 900, height: 1100 });
}
